import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  inject,
} from '@angular/core';

/**
 * Directive that displays any HTMLElement as a floating tooltip that follows the cursor.
 *
 * Attach it to the host element and pass the tooltip element reference via the input.
 * The tooltip element will be positioned using `position: fixed` and will follow the
 * cursor similar to the behaviour of the TooltipCharts component.
 */
@Directive({
  selector: '[libTooltipFollow]',
  standalone: true,
})
export class TooltipFollowDirective implements OnDestroy {
  /**
   * Tooltip element that will be shown while the user hovers the host element.
   */
  @Input('libTooltipFollow')
  set tooltip(value: ElementRef<HTMLElement> | HTMLElement | null | undefined) {
    this.tooltipElement = this.resolveElement(value);
    if (this.tooltipElement) {
      this.prepareTooltipElement();
    }
  }

  /** Horizontal offset applied to the tooltip position. */
  @Input() tooltipFollowOffsetX = 12;
  /** Vertical offset applied to the tooltip position. */
  @Input() tooltipFollowOffsetY = 12;
  /** Allows disabling the tooltip behaviour without removing the directive. */
  @Input() tooltipFollowDisabled = false;
  /** Custom z-index applied to the floating tooltip. */
  @Input() tooltipFollowZIndex = 1000;
  /** Delay in milliseconds before showing the tooltip. Default is 0 (no delay). */
  @Input() tooltipFollowDebounceTime = 0;

  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);

  private tooltipElement: HTMLElement | null = null;
  private tooltipInitialDisplay: string | null = null;
  private readonly windowRef = this.document?.defaultView ?? null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingMouseEvent: MouseEvent | null = null;

  private readonly handleMouseMove = (event: MouseEvent): void => {
    if (!this.tooltipElement) {
      return;
    }
    this.updateTooltipPosition(event);
  };

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {
    if (this.tooltipFollowDisabled || !this.tooltipElement || !this.windowRef) {
      return;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.pendingMouseEvent = event;

    if (this.tooltipFollowDebounceTime === 0) {
      this.showTooltip(event);
      this.windowRef.addEventListener('mousemove', this.handleMouseMove, { passive: true });
    } else {
      this.debounceTimer = setTimeout(() => {
        if (this.pendingMouseEvent) {
          this.showTooltip(this.pendingMouseEvent);
          this.windowRef?.addEventListener('mousemove', this.handleMouseMove, { passive: true });
        }
        this.debounceTimer = null;
        this.pendingMouseEvent = null;
      }, this.tooltipFollowDebounceTime);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.pendingMouseEvent = null;
    }
    this.hideTooltip();
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingMouseEvent = null;
    this.hideTooltip(true);
  }

  /** Ensure tooltip has the required base styles and cached display value. */
  private prepareTooltipElement(): void {
    if (!this.tooltipElement || !this.windowRef) {
      return;
    }

    const computedStyles = this.windowRef.getComputedStyle(this.tooltipElement);
    if (!this.tooltipInitialDisplay) {
      this.tooltipInitialDisplay = computedStyles.display !== 'none' ? computedStyles.display : 'block';
    }

    this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(this.tooltipElement, 'top', '0');
    this.renderer.setStyle(this.tooltipElement, 'left', '0');
    this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.tooltipElement, 'z-index', String(this.tooltipFollowZIndex));
    this.renderer.setStyle(this.tooltipElement, 'display', 'none');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');
    this.renderer.setStyle(this.tooltipElement, 'transform', 'translate3d(-9999px, -9999px, 0)');
  }

  /** Display tooltip and align it with the mouse cursor. */
  private showTooltip(event: MouseEvent): void {
    if (!this.tooltipElement) {
      return;
    }

    this.prepareTooltipElement();
    this.renderer.setStyle(this.tooltipElement, 'display', this.tooltipInitialDisplay ?? 'block');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'visible');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '1');

    this.updateTooltipPosition(event);
  }

  /** Hide tooltip and cleanup listeners. */
  private hideTooltip(forceCleanup: boolean = false): void {
    this.windowRef?.removeEventListener('mousemove', this.handleMouseMove);

    if (!this.tooltipElement) {
      return;
    }

    this.renderer.setStyle(this.tooltipElement, 'display', 'none');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');

    if (forceCleanup) {
      this.renderer.removeStyle(this.tooltipElement, 'transform');
      this.renderer.removeStyle(this.tooltipElement, 'pointer-events');
      this.renderer.removeStyle(this.tooltipElement, 'top');
      this.renderer.removeStyle(this.tooltipElement, 'left');
      this.renderer.removeStyle(this.tooltipElement, 'position');
      this.renderer.removeStyle(this.tooltipElement, 'z-index');
      if (this.tooltipInitialDisplay) {
        this.renderer.setStyle(this.tooltipElement, 'display', this.tooltipInitialDisplay);
      } else {
        this.renderer.removeStyle(this.tooltipElement, 'display');
      }
    }
  }

  /** Update tooltip coordinates based on current mouse position. */
  private updateTooltipPosition(event: MouseEvent): void {
    if (!this.tooltipElement) {
      return;
    }

    const left = event.clientX + this.tooltipFollowOffsetX;
    const top = event.clientY + this.tooltipFollowOffsetY;

    this.renderer.setStyle(this.tooltipElement, 'transform', `translate3d(${left}px, ${top}px, 0)`);
  }

  private resolveElement(value?: ElementRef<HTMLElement> | HTMLElement | null): HTMLElement | null {
    if (!value) {
      return null;
    }

    if (value instanceof ElementRef) {
      return value.nativeElement;
    }

    return value;
  }
}
